/**
 * Account roles — monday's four, plus the one that is ours (M0-F8 §5.3, T-9f).
 *
 * monday's account roles are exactly **Admin · Member · Viewer · Guest**
 * (`[ui:admin/permissions.png]`, `[sdl]` `BaseRoleName`), and after round 9f an `admin` on this
 * product has exactly monday's admin rights: account-level pages, user and team management and
 * board-ownership transfer, but **no** implicit access to a `share` board they do not subscribe
 * to (`discovery/first-look.md`: monday admins are not implicit members of shareable boards).
 *
 * `superadmin` is **not a monday role**. It is the break-glass account this deployment needs
 * because our admins now follow monday: one principal that can reach everything, audited like
 * any other. It never reaches a monday-shaped surface: `/v2` reports a superadmin as
 * `kind: "admin"` (deviation **D-9f-1**, specs/M9-public-api.md §7.2.2), `default_new_user_role`
 * never offers it, no invitation may carry it, and only a superadmin may grant or revoke it.
 */
import { z } from 'zod';
/** monday's four, in the order the admin UI lists them. */
export const MONDAY_ACCOUNT_ROLES = ['admin', 'member', 'viewer', 'guest'];
/** Strict: every surface that must stay monday-shaped parses with this one. */
export const mondayAccountRoleSchema = z.enum(MONDAY_ACCOUNT_ROLES);
/** Ours, and above `admin`. Stored verbatim in `users.role` (`varchar(16)`, no CHECK). */
export const SUPERADMIN_ROLE = 'superadmin';
export const ACCOUNT_ROLES = [...MONDAY_ACCOUNT_ROLES, SUPERADMIN_ROLE];
export const accountRoleSchema = z.enum(ACCOUNT_ROLES);
/** The label the admin Users directory renders. */
export const ACCOUNT_ROLE_LABELS = {
    superadmin: 'Super admin',
    admin: 'Admin',
    member: 'Member',
    viewer: 'Viewer',
    guest: 'Guest',
};
export const isSuperAdminRole = (role) => role === SUPERADMIN_ROLE;
/**
 * "May open Administration": an `admin` **or** a `superadmin`. Every `role === 'admin'` test in
 * the product goes through this, so adding the fifth role could never silently demote it.
 */
export const isAdminRole = (role) => role === 'admin' || isSuperAdminRole(role);
/**
 * `users.kind` is monday's free-form `User.kind` string and `/v2` serves it verbatim
 * (`specs/M9-public-api.md` §7.2.2). A superadmin is published as an ordinary `admin`: monday
 * has no fifth kind, and inventing one would leak a role monday's clients cannot parse
 * (CLAUDE.md rule 3). **D-9f-1.**
 */
export const userKindForRole = (role) => (isSuperAdminRole(role) ? 'admin' : role);
//# sourceMappingURL=roles.js.map